import { useState } from "react";
import { todayISO, toLocalISODate } from "@/lib/utils";
import PageMeta from "@/components/common/PageMeta";
import { reportService, type ReportType } from "@/services/reportService";
import { handleApiError } from "@/services/api";

const reportTypes: { key: ReportType; label: string; description: string }[] = [
  { key: "egg-production", label: "Egg Production", description: "Daily egg collection data by flock" },
  { key: "feed-logs", label: "Feed Distribution", description: "Feed given to each flock with formula details" },
  { key: "mortality", label: "Mortality", description: "Deaths by flock and cause" },
  { key: "egg-sales", label: "Egg Sales", description: "Sales with qty, rate, buyer, and totals" },
  { key: "stock", label: "Raw Material Stock", description: "Purchase and adjustment transactions" },
  { key: "feed-production", label: "Feed Production", description: "Feed batches with cost breakdown" },
  { key: "expenses", label: "Expenses", description: "All farm expenses by category" },
];

export default function ReportsPage() {
  const today = todayISO();
  const thirtyDaysAgo = toLocalISODate(new Date(Date.now() - 30 * 86400000));

  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to, setTo] = useState(today);
  const [downloading, setDownloading] = useState<ReportType | null>(null);
  const [error, setError] = useState("");

  const handleDownload = async (type: ReportType) => {
    setError("");
    setDownloading(type);
    try {
      await reportService.download(type, from, to);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      <PageMeta title="Reports | Poultry Book" description="Export farm data as CSV" />

      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-800 dark:text-white/90">Reports</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Export your farm data as CSV files for accounting and analysis.
        </p>
      </div>

      {/* Date Range */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm text-gray-600 dark:text-gray-400">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-error-50 px-4 py-3 text-sm text-error-600 dark:bg-error-500/10 dark:text-error-400">
          {error}
        </div>
      )}

      {/* Report Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {reportTypes.map((report) => (
          <div
            key={report.key}
            className="card p-5 sm:p-6"
          >
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">
              {report.label}
            </h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {report.description}
            </p>
            <button
              onClick={() => handleDownload(report.key)}
              disabled={downloading !== null}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-50"
            >
              {downloading === report.key ? (
                "Downloading..."
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download CSV
                </>
              )}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
